import { useEffect, useMemo, useState } from "react";
import {
  useInbox,
  useAcceptCapture,
  useRejectCapture,
  useDeferCapture,
  type SuggestedActionItem,
  type InboxEntry,
} from "@/hooks/use-inbox";
import { TriageCard } from "@/components/inbox/TriageCard";
import { EditTriageModal } from "@/components/inbox/EditTriageModal";
import { InboxEmptyState } from "@/components/inbox/InboxEmptyState";
import { useToast } from "@/components/system";
import { formatError } from "@/lib/format-error";

/**
 * BW19 PR-D — Inbox route.
 *
 * Lists awaiting_review captures in reverse-chronological order. Cards are
 * keyboard-driveable: A=accept, E=edit, R=reject, D=defer (1d), J/K=navigate.
 * Click an action button on a specific card to act without changing
 * keyboard focus.
 */

export function Inbox() {
  const { data, isLoading, error } = useInbox();
  const accept = useAcceptCapture();
  const reject = useRejectCapture();
  const defer = useDeferCapture();
  const toast = useToast();

  const entries = useMemo<InboxEntry[]>(() => data ?? [], [data]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [editing, setEditing] = useState<InboxEntry | null>(null);

  useEffect(() => {
    if (activeIdx >= entries.length) {
      setActiveIdx(Math.max(0, entries.length - 1));
    }
  }, [activeIdx, entries.length]);

  useEffect(() => {
    function isTextEntry(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return false;
    }

    function onKey(e: KeyboardEvent) {
      if (isTextEntry(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const entry = entries[activeIdx];
      if (!entry) return;

      const key = e.key.toLowerCase();
      if (key === "j") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(entries.length - 1, i + 1));
      } else if (key === "k") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (key === "a") {
        e.preventDefault();
        void doAccept(entry, entry.triage?.suggested_action_items ?? []);
      } else if (key === "e") {
        e.preventDefault();
        setEditing(entry);
      } else if (key === "r") {
        e.preventDefault();
        void doReject(entry);
      } else if (key === "d") {
        e.preventDefault();
        void doDefer(entry);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, activeIdx]);

  async function doAccept(entry: InboxEntry, items: SuggestedActionItem[]) {
    try {
      await accept.mutateAsync({
        capture_id: entry.capture.id,
        items,
        suggested_project_id: entry.triage?.suggested_project_id ?? null,
        suggested_thread_id: entry.triage?.suggested_thread_id ?? null,
      });
      toast.show(
        items.length === 0
          ? "Accepted with no action items."
          : `Promoted ${items.length} ${items.length === 1 ? "action" : "actions"} to /tasks.`,
      );
    } catch (err) {
      toast.show(`Accept failed: ${formatError(err)}`);
    }
  }

  async function doReject(entry: InboxEntry) {
    try {
      await reject.mutateAsync(entry.capture.id);
      toast.show("Rejected.");
    } catch (err) {
      toast.show(`Reject failed: ${formatError(err)}`);
    }
  }

  async function doDefer(entry: InboxEntry) {
    const until = new Date(Date.now() + 24 * 3600 * 1000).toISOString();
    try {
      await defer.mutateAsync({ capture_id: entry.capture.id, until });
      toast.show("Deferred for 1 day.");
    } catch (err) {
      toast.show(`Defer failed: ${formatError(err)}`);
    }
  }

  return (
    <article aria-labelledby="inbox-heading">
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: "var(--space-6)",
        }}
      >
        <h1 id="inbox-heading" style={{ margin: 0, fontSize: 22, color: "var(--fg)" }}>
          Inbox
        </h1>
        <span style={{ color: "var(--fg-2)", fontSize: 13 }}>
          {entries.length} awaiting review - shortcuts: J/K, A, E, R, D
        </span>
      </header>

      {isLoading && <p style={{ color: "var(--fg-2)" }}>Loading inbox...</p>}
      {error && <p style={{ color: "var(--danger, #c53030)" }}>{formatError(error)}</p>}
      {!isLoading && !error && entries.length === 0 && <InboxEmptyState />}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {entries.map((entry, idx) => (
          <TriageCard
            key={entry.capture.id}
            entry={entry}
            active={idx === activeIdx}
            onActivate={() => setActiveIdx(idx)}
            onAccept={(items) => void doAccept(entry, items)}
            onEdit={() => setEditing(entry)}
            onReject={() => void doReject(entry)}
            onDefer={() => void doDefer(entry)}
          />
        ))}
      </div>

      {editing && (
        <EditTriageModal
          open
          initialItems={editing.triage?.suggested_action_items ?? []}
          onClose={() => setEditing(null)}
          onSave={async (items) => {
            const target = editing;
            setEditing(null);
            await doAccept(target, items);
          }}
        />
      )}
    </article>
  );
}
